import { useState } from 'react';
import { useCustom, useCustomMutation, useList } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronDown, Plus, Trash2, Loader2, Languages, Subtitles, Volume2, Film } from 'lucide-react';
import { notify } from '@/lib/notify';
import { resolveApiUrl } from '@/providers/api-url';
import { uploadFile } from '@/lib/upload';

const API_URL = resolveApiUrl();

interface MediaRecord {
  id: number;
  moduleItemId: number;
  languageCode: string;
  mediaType: 'SUBTITLE' | 'AUDIO' | 'VIDEO';
  fileId: number | null;
  url: string | null;
  status: 'PENDING' | 'PROCESSING' | 'READY' | 'ERROR' | null;
  processingError: string | null;
  isDefault: boolean;
  order: number;
}

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
}

const MEDIA_TYPE_CONFIG = {
  SUBTITLE: { label: 'Субтитры', icon: Subtitles, accept: '.vtt,.srt', color: 'text-blue-600' },
  AUDIO: { label: 'Аудио', icon: Volume2, accept: '.mp3,.aac,.wav,.m4a', color: 'text-green-600' },
  VIDEO: { label: 'Видео', icon: Film, accept: '.mp4,.webm,.mov', color: 'text-purple-600' },
} as const;

const STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' }> = {
  READY: { label: 'Готово', variant: 'default' },
  PENDING: { label: 'В очереди', variant: 'secondary' },
  PROCESSING: { label: 'Обработка', variant: 'secondary' },
  ERROR: { label: 'Ошибка', variant: 'destructive' },
};

/**
 * Извлекает массив MediaRecord из ответа API.
 * Data provider может вернуть данные как массив напрямую
 * или обёрнутыми в { data: [...] } в зависимости от формата ответа.
 */
function extractMediaList(rawData: MediaRecord[] | { data: MediaRecord[] } | undefined): MediaRecord[] {
  if (Array.isArray(rawData)) {return rawData;}
  if (rawData && typeof rawData === 'object' && 'data' in rawData && Array.isArray(rawData.data)) {
    return rawData.data;
  }
  return [];
}

interface ModuleItemMediaSectionProps {
  moduleItemId: string;
}

export function ModuleItemMediaSection({ moduleItemId }: ModuleItemMediaSectionProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MediaRecord | null>(null);
  const [addingLang, setAddingLang] = useState<string | null>(null);
  const [addingType, setAddingType] = useState<'SUBTITLE' | 'AUDIO' | 'VIDEO'>('SUBTITLE');
  const [uploading, setUploading] = useState(false);

  const { result: langResult } = useList<LanguageRecord>({ resource: 'languages' });
  const languages = langResult?.data ?? [];

  const { query: mediaQuery } = useCustom<MediaRecord[]>({
    url: `${API_URL}/module-item-media/${moduleItemId}`,
    method: 'get',
  });

  const rawData = mediaQuery.data?.data;
  const mediaList: MediaRecord[] = extractMediaList(rawData);

  const createMutation = useCustomMutation();
  const deleteMutation = useCustomMutation();

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['data'] });
  };

  // Group media by language
  const mediaByLang = new Map<string, MediaRecord[]>();
  for (const m of mediaList) {
    const arr = mediaByLang.get(m.languageCode) ?? [];
    arr.push(m);
    mediaByLang.set(m.languageCode, arr);
  }

  const handleFileUpload = async (file: File, languageCode: string, mediaType: 'SUBTITLE' | 'AUDIO' | 'VIDEO') => {
    setUploading(true);
    try {
      const uploaded = await uploadFile({
        file,
        access: 'PRIVATE',
      });

      // Create media record
      createMutation.mutate(
        {
          url: `${API_URL}/module-item-media`,
          method: 'post',
          values: {
            moduleItemId: Number(moduleItemId),
            languageCode,
            mediaType,
            fileId: uploaded.id,
            url: uploaded.url,
          },
        },
        {
          onSuccess: () => {
            notify.success(`${MEDIA_TYPE_CONFIG[mediaType].label} загружены`);
            refetch();
            setAddingLang(null);
          },
          onError: () => {
            notify.error('Ошибка создания медиа');
          },
        },
      );
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) {return;}
    deleteMutation.mutate(
      {
        url: `${API_URL}/module-item-media/${deleteTarget.id}`,
        method: 'delete',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Медиа удалено');
          setDeleteTarget(null);
          refetch();
        },
        onError: () => {
          notify.error('Ошибка удаления');
        },
      },
    );
  };

  const mediaCount = mediaList.length;

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <span className="flex items-center gap-2">
              <Languages className="h-4 w-4" />
              Локализация
              {mediaCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {mediaCount}
                </Badge>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3 pt-2">
            {mediaByLang.size === 0 && !addingLang ? (
              <p className="text-xs text-muted-foreground text-center py-3">
                Нет дополнительных медиа. Добавьте субтитры, аудиодорожку или видео для других языков.
              </p>
            ) : (
              [...mediaByLang.entries()].map(([langCode, items]) => {
                const lang = languages.find((l) => l.code === langCode);
                const langName = lang?.nativeName ?? langCode.toUpperCase();

                return (
                  <div key={langCode} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">{langName}</span>
                      <Badge variant="outline" className="text-[10px]">{langCode}</Badge>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((media) => {
                        const config = MEDIA_TYPE_CONFIG[media.mediaType];
                        const Icon = config.icon;
                        const statusInfo = media.status ? STATUS_BADGES[media.status] : null;

                        return (
                          <div key={media.id} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                              <span>{config.label}</span>
                              {statusInfo && (
                                <Badge variant={statusInfo.variant} className="text-[10px] px-1.5 py-0">
                                  {statusInfo.label}
                                </Badge>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive"
                              onClick={() => setDeleteTarget(media)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}

            {/* Add new media form */}
            {addingLang !== null ? (
              <div className="rounded-lg border border-dashed p-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Язык</Label>
                    <Select value={addingLang} onValueChange={setAddingLang}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((l) => (
                          <SelectItem key={l.code} value={l.code}>
                            {l.nativeName} ({l.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Тип</Label>
                    <Select value={addingType} onValueChange={(v) => setAddingType(v as typeof addingType)}>
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SUBTITLE">Субтитры (.vtt, .srt)</SelectItem>
                        <SelectItem value="AUDIO">Аудиодорожка (.mp3)</SelectItem>
                        <SelectItem value="VIDEO">Видео (.mp4)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept={MEDIA_TYPE_CONFIG[addingType].accept}
                    className="text-xs h-8"
                    disabled={uploading || !addingLang}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file && addingLang) {
                        void handleFileUpload(file, addingLang, addingType);
                      }
                    }}
                  />
                  {uploading && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs w-full"
                  onClick={() => setAddingLang(null)}
                >
                  Отмена
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs"
                onClick={() => setAddingLang(languages[0]?.code ?? 'en')}
              >
                <Plus className="h-3 w-3 mr-1" />
                Добавить медиа
              </Button>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить медиа?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  {MEDIA_TYPE_CONFIG[deleteTarget.mediaType].label} ({deleteTarget.languageCode.toUpperCase()}) будут удалены.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
